import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CheckCheck, Settings } from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import notificationService from '../services/notificationService';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications({ page, limit: 20 });
      
      if (page === 1) {
        setNotifications(response.results || []);
      } else {
        setNotifications(prev => [...prev, ...(response.results || [])]);
      }
      
      setHasMore(!!response.next);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      appointment: '📅',
      order: '📦',
      payment: '💳',
      consultation: '💬',
      reminder: '🔔',
      promotion: '🎉',
      system: 'ℹ️'
    };
    return iconMap[type] || '🔔';
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <>
      <Header />
      <div className="notifications-page">
        <div className="notifications-container">
          <div className="notifications-header">
            <button onClick={() => navigate(-1)} className="back-button">
              <ArrowLeft size={20} />
            </button>
            <h1>Notifications</h1>
            <button 
              onClick={() => navigate('/notifications/settings')} 
              className="settings-button"
              aria-label="Notification Settings"
            >
              <Settings size={20} />
            </button>
          </div>

          {unreadCount > 0 && (
            <div className="notifications-actions">
              <span className="unread-info">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </span>
              <button 
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
                className="mark-all-read-button"
              >
                <CheckCheck size={16} />
                {markingAllRead ? 'Marking...' : 'Mark all as read'}
              </button>
            </div>
          )}

          <div className="notifications-list">
            {loading && page === 1 ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔔</div>
                <h3>No notifications yet</h3>
                <p>When you have notifications, they'll appear here</p>
              </div>
            ) : (
              <>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-card ${!notification.is_read ? 'unread' : ''}`}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="notification-body">
                      <div className="notification-header-row">
                        <h3 className="notification-title">{notification.title}</h3>
                        {!notification.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="mark-read-button"
                            title="Mark as read"
                          >
                            <Check size={16} />
                          </button>
                        )}
                      </div>
                      <p className="notification-message">{notification.message}</p>
                      <div className="notification-meta">
                        <span className="notification-time">
                          {formatDateTime(notification.created_at)}
                        </span>
                        {notification.notification_type && (
                          <span className="notification-type-badge">
                            {notification.notification_type}
                          </span>
                        )}
                      </div>
                    </div>
                    {!notification.is_read && <div className="unread-indicator"></div>}
                  </div>
                ))}

                {hasMore && (
                  <div className="load-more-container">
                    <button 
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="load-more-button"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Notifications;
